import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import {
  Button,
  Chip,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, Box } from "@mui/system";
import { AttachFileOutlined, InsertDriveFileOutlined, SendOutlined } from "@mui/icons-material";
import useStyles from "./styles";
import StyledSuggestionRatingComponent from "../../styledSuggestionRatingComponent";

export default function SuggestionAvaliacaoComponent({ form = {} }) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [answers, setAnswers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId);
      if (existing) {
        return prev.map((a) => (a.questionId === questionId ? { ...a, answer: value } : a));
      }
      return [...prev, { questionId, answer: value }];
    });
  };

  const toggleChipSelecionado = (questionId, optionValue) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId);
      let currentArray = existing && Array.isArray(existing.answer) ? existing.answer : [];
      
      if (currentArray.includes(optionValue)) {
        currentArray = currentArray.filter((item) => item !== optionValue);
      } else {
        currentArray = [...currentArray, optionValue];
      }
      
      if (existing) {
        return prev.map((a) => (a.questionId === questionId ? { ...a, answer: currentArray } : a));
      }
      return [...prev, { questionId, answer: currentArray }];
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newFiles = files.map((file) => ({
      name: file.name,
      size: file.size,
    }));
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    e.target.value = null;
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={styles.formCard}>
      <Box sx={styles.header}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
            {form?.title || "Sugestões de Melhoria para a Plataforma"}
          </Typography>
          <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.2 }}>
            Deixe sua sugestão e nos ajude a evoluir a plataforma continuamente.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.content}>
        {Array.isArray(form?.questions) &&
          form?.questions?.length > 0 &&
          form?.questions?.map((q, index) => {
            const questionId = q?.id || index;
            return (
              <Box key={questionId} sx={styles.questionBox}>
                <Typography sx={styles.questionTitle}>
                  {q?.question || "Sua pergunta aparecerá aqui"}
                  <span style={{ color: theme.palette.error.main, marginLeft: 4 }}>*</span>
                </Typography>

                {q?.type === 0 && (
                  <StyledSuggestionRatingComponent
                    questionId={questionId}
                    rating={answers}
                    setRating={setAnswers}
                  />
                )}

                {q?.type === 1 && (
                  <TextField
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Escreva sua resposta aqui..."
                    fullWidth
                    value={answers.find((a) => a.questionId === questionId)?.answer || ""}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: theme.palette.background.paper,
                        borderRadius: "10px",
                      },
                    }}
                  />
                )}

                {q?.type === 2 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      padding: "14px",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      mt: 1,
                      borderRadius: "10px",
                      background: theme.palette.background.default,
                      borderColor: theme.palette.divider,
                    }}
                  >
                    {q?.options?.map((option, idxOpt) => {
                      const optValue = option?.option || option?.value || option;
                      const currentAnswer = answers.find((a) => a.questionId === questionId)?.answer || [];
                      const isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(optValue);

                      return (
                        <Chip
                          key={optValue || idxOpt}
                          label={optValue}
                          size="medium"
                          clickable
                          onClick={() => toggleChipSelecionado(questionId, optValue)}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            transition: "all 0.2s",
                            borderRadius: "8px",
                            fontWeight: 600,
                            ...(isSelected
                              ? {
                                  bgcolor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                }
                              : {
                                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                                  borderColor: alpha(theme.palette.primary.main, 0.2),
                                }),
                          }}
                        />
                      );
                    })}
                  </Paper>
                )}
              </Box>
            );
          })}

        {/* Seção de Anexos (Qualquer tipo de arquivo) */}
        <Box sx={styles.questionBox}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AttachFileOutlined sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={styles.questionTitle}>
              Anexar Arquivos
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.3 }}>
            Você pode anexar qualquer tipo de arquivo (imagens, documentos, planilhas, PDFs, logs, etc.) para ilustrar sua sugestão.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
            <Button
              component="label"
              variant="contained"
              startIcon={<AttachFileOutlined />}
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                py: 0.8,
                boxShadow: "none",
              }}
            >
              Adicionar Arquivos
              <input
                type="file"
                multiple
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, display: "block", mb: 1, textTransform: "uppercase" }}>
                Arquivos Pronto Para Envio ({selectedFiles.length}):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
                {selectedFiles.map((file, index) => {
                  const ext = file.name?.split(".")?.pop()?.toUpperCase() || "ARQUIVO";
                  return (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        p: 1,
                        pr: 1.5,
                        borderRadius: "8px",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.05)
                            : alpha(theme.palette.primary.main, 0.03),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "6px",
                          bgcolor: alpha(theme.palette.primary.main, 0.14),
                          color: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <InsertDriveFileOutlined sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {file.name}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                          <Chip
                            label={ext}
                            size="small"
                            sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, borderRadius: "4px" }}
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.68rem" }}>
                            {(file.size / 1024).toFixed(1)} KB
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label="Remover"
                        size="small"
                        clickable
                        onClick={() => removeFile(index)}
                        color="error"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, borderRadius: "6px", ml: 0.5 }}
                      />
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={styles.footer}>
        <Button
          variant="contained"
          color="primary"
          disabled
          sx={styles.buttonSubmit}
          endIcon={<SendOutlined />}
        >
          Enviar Sugestão
        </Button>
      </Box>
    </Box>
  );
}
